import { useTranslation } from 'react-i18next';

import { ContractReviewLine, useGetPricesQuery, useGetSpacesQuery } from '@client/api';
import { Input, Label, Select } from '@client/toolkit';

import { FieldSourceTag } from './field-source-tag';
import type { ManualLineCorrectionPayload } from '@client/api';

interface ReviewLineEditorProps {
  line: ContractReviewLine;
  index: number;
  buildingId: string;
  draft: ManualLineCorrectionPayload;
  onChange: (draft: ManualLineCorrectionPayload) => void;
}

/**
 * Field-by-field editor for a single <c>AgreementImportLineEntity</c> (Phase 4 review UI).
 * Space/price pickers are scoped to the building the import session is bound to.
 */
export function ReviewLineEditor({ line, index, buildingId, draft, onChange }: ReviewLineEditorProps) {
  const { t: tAgreements } = useTranslation('agreements');

  const { data: spacesData } = useGetSpacesQuery({ 'rootId.Equal': buildingId, 'pagination.Limit': 250 });
  const { data: pricesData } = useGetPricesQuery({ 'pagination.Limit': 250 });

  const spaces = spacesData?.items ?? [];
  const prices = pricesData?.items ?? [];

  return (
    <div className="rounded-md border border-white/10 p-4 space-y-3">
      <div className="font-medium text-sm text-white/80">
        {tAgreements('agreementImport.review.lineFields', { index: index + 1 })} —{' '}
        {line.rawSourceData.spaceName ?? tAgreements('agreementImport.review.unnamedSpace')}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{tAgreements('agreementImport.review.space')}</Label>
          <div className="flex items-center gap-2">
            <Select
              size="medium"
              value={draft.spaceId ?? ''}
              onChange={(e) => onChange({ ...draft, spaceId: e.target.value || null })}
            >
              <option value="">—</option>
              {spaces.map((space) => (
                <option key={space.id} value={space.id}>
                  {space.name}
                </option>
              ))}
            </Select>
            <FieldSourceTag source={line.fieldSources.spaceId} />
          </div>
        </div>

        <div>
          <Label>{tAgreements('agreementImport.review.price')}</Label>
          <div className="flex items-center gap-2">
            <Select
              size="medium"
              value={draft.priceId ?? ''}
              onChange={(e) => onChange({ ...draft, priceId: e.target.value || null })}
            >
              <option value="">—</option>
              {prices.map((price) => (
                <option key={price.id} value={price.id}>
                  {price.product?.name} — {price.unitAmount?.amount} {price.unitAmount?.currency?.code}
                </option>
              ))}
            </Select>
            <FieldSourceTag source={line.fieldSources.priceId} />
          </div>
        </div>

        <div>
          <Label>{tAgreements('agreementImport.review.overrideArea')}</Label>
          <Input
            type="number"
            size="medium"
            value={draft.overrideArea ?? ''}
            onChange={(e) =>
              onChange({ ...draft, overrideArea: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
        </div>

        <div>
          <Label>{tAgreements('agreementImport.review.overwrittenUnitAmount')}</Label>
          <Input
            type="number"
            size="medium"
            value={draft.overwrittenUnitAmount ?? ''}
            onChange={(e) =>
              onChange({ ...draft, overwrittenUnitAmount: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
        </div>
      </div>
    </div>
  );
}
